'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface SeasonIndicatorProps {
  className?: string;
}

interface MonthData {
  month: string;
  season: string;
  waterTemp: string;
  visibility: string;
  conditions: string;
  rating: 'excellent' | 'good' | 'fair';
  highlights: string[];
  tip: string;
}

const MONTH_DATA: MonthData[] = [
  {
    month: 'January',
    season: 'Winter',
    waterTemp: '17–18°C',
    visibility: '15–25m',
    conditions: 'Cooler water, changeable surface conditions. Fewer divers.',
    rating: 'fair',
    highlights: ['Groupers', 'Amberjack', 'Conger Eels', 'Dusky Grouper'],
    tip: 'Wear a 7mm wetsuit. Shore dives in sheltered bays are most reliable.',
  },
  {
    month: 'February',
    season: 'Winter',
    waterTemp: '16–17°C',
    visibility: '15–20m',
    conditions: 'Coldest month. Seas can be rough but visibility often improves.',
    rating: 'fair',
    highlights: ['Groupers', 'Moray Eels', 'Nudibranchs'],
    tip: 'Best for macro photography. Fewer boats, more marine life activity.',
  },
  {
    month: 'March',
    season: 'Spring',
    waterTemp: '17–19°C',
    visibility: '15–25m',
    conditions: 'Seas calming. Water warming. Good beginner conditions emerging.',
    rating: 'good',
    highlights: ['Octopus', 'Sea Bream', 'Moray Eels'],
    tip: 'Great time to start the season. Zenobia is excellent year-round.',
  },
  {
    month: 'April',
    season: 'Spring',
    waterTemp: '18–21°C',
    visibility: '20–28m',
    conditions: 'Calm seas, warming water. Excellent for all levels.',
    rating: 'good',
    highlights: ['Octopus', 'Groupers', 'Sea Turtles beginning to appear'],
    tip: 'One of the best months for photography — clear water, active marine life.',
  },
  {
    month: 'May',
    season: 'Spring',
    waterTemp: '21–24°C',
    visibility: '20–30m',
    conditions: 'Warm, calm, clear. Peak spring diving.',
    rating: 'excellent',
    highlights: ['Sea Turtles', 'Barracuda', 'Groupers', 'Octopus'],
    tip: 'Excellent conditions across all regions. Book dive centres in advance.',
  },
  {
    month: 'June',
    season: 'Summer',
    waterTemp: '24–26°C',
    visibility: '25–35m',
    conditions: 'Warm, clear water. Peak season begins. Occasional plankton blooms.',
    rating: 'excellent',
    highlights: ['Barracuda', 'Tuna', 'Damselfish', 'Sea Turtles'],
    tip: 'Best visibility of the year. Dive early morning to avoid boat traffic.',
  },
  {
    month: 'July',
    season: 'Summer',
    waterTemp: '26–28°C',
    visibility: '25–35m',
    conditions: 'Peak summer. Warmest water. Busy dive centres.',
    rating: 'excellent',
    highlights: ['Barracuda', 'Parrotfish', 'Tuna', 'Damselfish'],
    tip: 'Book ahead. Early morning dives avoid crowds and surface glare.',
  },
  {
    month: 'August',
    season: 'Summer',
    waterTemp: '27–29°C',
    visibility: '20–30m',
    conditions: 'Hottest month. Very warm water. Busiest period.',
    rating: 'excellent',
    highlights: ['Barracuda', 'Tuna', 'Sea Turtles', 'Parrotfish'],
    tip: 'Hydrate well. Night dives are spectacular in August.',
  },
  {
    month: 'September',
    season: 'Autumn',
    waterTemp: '26–28°C',
    visibility: '25–30m',
    conditions: 'Warm water, fewer crowds, very stable conditions.',
    rating: 'excellent',
    highlights: ['Rays', 'Seahorses', 'Cuttlefish', 'Nudibranchs'],
    tip: 'Best month overall — warm water, great visibility, fewer tourists.',
  },
  {
    month: 'October',
    season: 'Autumn',
    waterTemp: '23–26°C',
    visibility: '20–28m',
    conditions: 'Excellent diving. Water still warm. Seas very calm.',
    rating: 'excellent',
    highlights: ['Rays', 'Cuttlefish', 'Seahorses', 'Groupers'],
    tip: 'Ideal for the Zenobia. Visibility and conditions are superb.',
  },
  {
    month: 'November',
    season: 'Autumn',
    waterTemp: '20–23°C',
    visibility: '15–25m',
    conditions: 'Cooling water. Conditions variable. Quieter dive centres.',
    rating: 'good',
    highlights: ['Groupers', 'Moray Eels', 'Nudibranchs'],
    tip: 'Good value month. Fewer divers, attentive service from dive centres.',
  },
  {
    month: 'December',
    season: 'Winter',
    waterTemp: '18–20°C',
    visibility: '15–22m',
    conditions: 'Cooler water, occasional rough seas. Quietest period.',
    rating: 'fair',
    highlights: ['Groupers', 'Amberjack', 'Conger Eels'],
    tip: 'Shore dives in sheltered spots work well. 7mm wetsuit recommended.',
  },
];

const RATING_CONFIG = {
  excellent: {
    label: 'Excellent',
    color: '#3FD896',
    bg: 'rgba(63,216,150,0.12)',
    border: 'rgba(63,216,150,0.30)',
    bars: 3,
  },
  good: {
    label: 'Good',
    color: '#6FD3E0',
    bg: 'rgba(111,211,224,0.12)',
    border: 'rgba(111,211,224,0.30)',
    bars: 2,
  },
  fair: {
    label: 'Fair',
    color: '#FFB84D',
    bg: 'rgba(255,184,77,0.10)',
    border: 'rgba(255,184,77,0.25)',
    bars: 1,
  },
};

const SeasonIndicator = ({ className = '' }: SeasonIndicatorProps) => {
  const [currentMonthIndex, setCurrentMonthIndex] = useState<number>(0);

  useEffect(() => {
    setCurrentMonthIndex(new Date().getMonth());
  }, []);

  const data = MONTH_DATA[currentMonthIndex];
  const rating = RATING_CONFIG[data.rating];

  return (
    <section
      className={`py-10 sm:py-12 ${className}`}
      style={{
        background: '#071a24',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <p
                className="text-xs font-semibold uppercase tracking-widest mb-1"
                style={{ color: '#FF8A3D' }}
              >
                Best Time to Dive Cyprus Right Now
              </p>
              <h2
                className="text-xl sm:text-2xl font-headline font-bold"
                style={{ color: '#EAF4F8' }}
              >
                {data.month} — {data.season} Diving
              </h2>
            </div>
            {/* Rating badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full self-start sm:self-auto"
              style={{
                background: rating.bg,
                border: `1px solid ${rating.border}`,
              }}
            >
              <div className="flex gap-0.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-4 rounded-sm"
                    style={{
                      background: i < rating.bars ? rating.color : 'rgba(255,255,255,0.12)',
                    }}
                  />
                ))}
              </div>
              <span className="text-sm font-semibold" style={{ color: rating.color }}>
                {rating.label} Conditions
              </span>
            </div>
          </div>

          {/* Main card */}
          <div
            className="rounded-2xl p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6"
            style={{
              background: '#0F2234',
              border: '1px solid rgba(111,211,224,0.12)',
            }}
          >
            {/* Stats */}
            <div className="sm:col-span-1 flex sm:flex-col gap-4 sm:gap-5">
              <div>
                <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#6B8FA0' }}>
                  Water Temp
                </p>
                <p className="text-lg font-bold font-headline" style={{ color: '#6FD3E0' }}>
                  {data.waterTemp}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#6B8FA0' }}>
                  Visibility
                </p>
                <p className="text-lg font-bold font-headline" style={{ color: '#6FD3E0' }}>
                  {data.visibility}
                </p>
              </div>
            </div>

            {/* Conditions + tip */}
            <div className="sm:col-span-2">
              <p className="text-sm sm:text-base mb-3" style={{ color: '#C8DDE6' }}>
                {data.conditions}
              </p>

              {/* Marine life highlights */}
              <div className="flex flex-wrap gap-2 mb-4">
                {data.highlights.map((h) => (
                  <span
                    key={h}
                    className="text-xs px-2.5 py-1 rounded-full"
                    style={{
                      background: 'rgba(111,211,224,0.08)',
                      color: '#6FD3E0',
                      border: '1px solid rgba(111,211,224,0.18)',
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {/* Tip */}
              <div
                className="flex items-start gap-2.5 rounded-lg p-3"
                style={{
                  background: 'rgba(255,138,61,0.07)',
                  border: '1px solid rgba(255,138,61,0.18)',
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#FF8A3D"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p className="text-xs sm:text-sm" style={{ color: '#C8DDE6' }}>
                  <strong style={{ color: '#FF8A3D' }}>Tip: </strong>
                  {data.tip}
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-4 text-center">
            <Link
              href="/cyprus-diving-information"
              className="text-sm font-medium transition-opacity hover:opacity-80"
              style={{ color: '#6FD3E0' }}
            >
              Full seasonal diving guide →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeasonIndicator;
